import { FmlxAccordion, FmlxCheckBox, FmlxModal, FmlxTab } from 'fmlx-common-ui';
import React, { useCallback, useMemo, useState } from 'react';

export type FilterModalItem = {
  id: number;
  field: string;
  groupName?: string;
  disabled?: boolean;
  displayText?: string;
  defaultCombinator?: string;
  defaultValue?: string;
  isDropdown?: boolean;
  constraintType?: number;
  parentId?: number;
};

type AddFilterModalProps = {
  id: string;
  open: boolean;
  basicFilters: FilterModalItem[];
  advancedFilters: FilterModalItem[];
  existingBasicFilterIds?: Set<number>;
  onConfirm: (selected: FilterModalItem[]) => void;
  onCancel: () => void;
};

export function AddFilterModal(props: AddFilterModalProps) {
  const {
    id,
    open,
    basicFilters,
    advancedFilters,
    existingBasicFilterIds = new Set<number>(),
    onConfirm,
    onCancel,
  } = props;

  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedBasicFilterIds, setSelectedBasicFilterIds] = useState<Set<number>>(
    new Set<number>(),
  );
  const [selectedAdvancedFilterIds, setSelectedAdvancedFilterIds] = useState<Set<number>>(
    new Set<number>(),
  );

  // map of parent filter and its child filters (currently only used by ScoreGroup and Score)
  const basicChildrenMap = useMemo(() => {
    const map = new Map<number, FilterModalItem[]>();
    for (const filter of basicFilters) {
      if (filter.parentId != null) {
        const children = map.get(filter.parentId) ?? [];
        children.push(filter);
        map.set(filter.parentId, children);
      }
    }
    return map;
  }, [basicFilters]);

  // to check if basic filter is either already in the IMT or is selected in modal
  const isBasicFilterAvailable = useCallback(
    (filter: FilterModalItem): boolean => {
      if (filter.parentId == null) {
        return true;
      }
      return (
        selectedBasicFilterIds.has(filter.parentId) ||
        existingBasicFilterIds.has(filter.parentId)
      );
    },
    [selectedBasicFilterIds, existingBasicFilterIds],
  );

  // to check if basic filter is already in the IMT
  const isBasicFilterAlreadyInQuery = useCallback(
    (filterId: number): boolean => {
      return existingBasicFilterIds.has(filterId);
    },
    [existingBasicFilterIds],
  );

  // list of basic filters that can be checked
  // basic filters that already exists in the IMT cannot be re-selected
  const selectableBasicFilters = useMemo(() => {
    return basicFilters.filter((f) => !f.disabled && !isBasicFilterAlreadyInQuery(f.id));
  }, [basicFilters, isBasicFilterAlreadyInQuery]);

  const getAllSelectableIds = useCallback((): Set<number> => {
    const result = new Set<number>();
    for (const filter of selectableBasicFilters) {
      result.add(filter.id);
    }
    return result;
  }, [selectableBasicFilters]);

  // memoized selectable basic filter ids
  const allSelectableIds = useMemo(() => getAllSelectableIds(), [getAllSelectableIds]);

  // for counting how many selectable basic filters are currently selected
  const selectedCountFromSelectable = useMemo(() => {
    let count = 0;
    for (const filterId of allSelectableIds) {
      if (selectedBasicFilterIds.has(filterId)) {
        count++;
      }
    }
    return count;
  }, [allSelectableIds, selectedBasicFilterIds]);

  const areAllBasicFiltersSelected =
    allSelectableIds.size > 0 && selectedCountFromSelectable === allSelectableIds.size;
  const isBasicFilterPartiallySelected =
    selectedCountFromSelectable > 0 &&
    selectedCountFromSelectable < allSelectableIds.size;

  const onToggleSelectAll = () => {
    if (areAllBasicFiltersSelected) {
      // Unselect all selectable filters
      setSelectedBasicFilterIds((prev) => {
        const next = new Set(prev);
        for (const filterId of allSelectableIds) {
          next.delete(filterId);
        }
        return next;
      });
    } else {
      // Select all selectable filters
      setSelectedBasicFilterIds((prev) => {
        const next = new Set(prev);
        for (const filterId of allSelectableIds) {
          next.add(filterId);
        }
        return next;
      });
    }
  };

  const clearSelectedFilters = () => {
    setSelectedBasicFilterIds(new Set<number>());
    setSelectedAdvancedFilterIds(new Set<number>());
  };

  const onConfirmClicked = () => {
    const selectedFilters: FilterModalItem[] = [];

    selectedFilters.push(
      ...Array.from(selectedBasicFilterIds)
        .map((index: number) => {
          return basicFilters.find((basicFilter) => basicFilter.id === index);
        })
        .filter((item): item is FilterModalItem => item !== undefined),
    );

    selectedFilters.push(
      ...Array.from(selectedAdvancedFilterIds)
        .map((index: number) => {
          return advancedFilters.find((advFilter) => advFilter.id === index);
        })
        .filter((item): item is FilterModalItem => item !== undefined),
    );

    onConfirm(selectedFilters);
    clearSelectedFilters();
  };

  const onCancelClicked = () => {
    clearSelectedFilters();
    onCancel();
  };

  const onToggleBasicFilter = (index: number) => {
    setSelectedBasicFilterIds((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
        const children = basicChildrenMap.get(index);
        if (children) {
          for (const child of children) {
            if (!existingBasicFilterIds.has(index)) {
              next.delete(child.id);
            }
          }
        }
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const onToggleAdvancedFilter = (index: number) => {
    setSelectedAdvancedFilterIds((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const isFilterChecked = (index: number, isBasicFilter: boolean) => {
    return isBasicFilter
      ? selectedBasicFilterIds.has(index)
      : selectedAdvancedFilterIds.has(index);
  };

  const tabFooter = useMemo(() => {
    return (
      <div className="flex flex-row gap-3 items-center">
        <div>Selected Filters</div>
        <div>
          Basic
          <span
            id="addfiltermodal-span-selectedfilters-basic"
            data-testid="addfiltermodal-span-selectedfilters-basic"
            className="ml-1 font-bold text-blue-600"
          >
            {selectedBasicFilterIds.size}
          </span>
        </div>
        <div>
          Advanced
          <span
            id="addfiltermodal-span-selectedfilters-advanced"
            data-testid="addfiltermodal-span-selectedfilters-advanced"
            className="ml-1 font-bold text-blue-600"
          >
            {selectedAdvancedFilterIds.size}
          </span>
        </div>
      </div>
    );
  }, [selectedBasicFilterIds, selectedAdvancedFilterIds]);

  const advancedFilterAccordions = useMemo(() => {
    const groupedAdvancedFilters = advancedFilters?.reduce<
      Record<string, FilterModalItem[]>
    >((groups, filter) => {
      (groups[filter.groupName] ??= []).push(filter);
      return groups;
    }, {});

    const accordionItems = () => {
      let index = 0;
      return Object.entries(groupedAdvancedFilters ?? {})
        .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
        .flatMap(([group, filters]) => {
          const sortedFilters = filters.sort((a, b) =>
            (a.displayText ?? a.field).localeCompare(b.displayText ?? b.field),
          );
          return {
            id: index++,
            title: group,
            content: sortedFilters.map((filter) => {
              return (
                <div
                  id={`addfiltermodal-advancedfilter-${filter.id}-row`}
                  data-testid={`addfiltermodal-advancedfilter-${filter.id}-row`}
                  key={filter.id}
                  className="flex flex-row items-center gap-x-2 gap-y-4"
                >
                  <div
                    id={`addfiltermodal-advancedfilter-${filter.id}-displaytext`}
                    data-testid={`addfiltermodal-advancedfilter-${filter.id}-displaytext`}
                    className="flex-1 text-left text-sm"
                  >
                    {filter.displayText ?? filter.field}
                  </div>
                  <div className="shrink-0">
                    <FmlxCheckBox
                      id={`addfiltermodal-advancedfilter-${filter.id}-checkbox`}
                      checked={isFilterChecked(filter.id, false)}
                      disabled={filter.disabled ?? false}
                      onChange={() => onToggleAdvancedFilter(filter.id)}
                    />
                  </div>
                </div>
              );
            }) as any,
            disabled: false,
          };
        });
    };

    return (
      <FmlxAccordion
        id={`addfiltermodal-advancedfilter-accordions`}
        items={accordionItems()}
        multipleExpand
      />
    );
  }, [advancedFilters, selectedAdvancedFilterIds]);

  const basicFilterCheckboxes = useMemo(() => {
    const sortedFilters: FilterModalItem[] = [];
    const childMap = new Map<number, FilterModalItem[]>();

    for (const filter of basicFilters) {
      if (filter.parentId != null) {
        const children = childMap.get(filter.parentId) ?? [];
        children.push(filter);
        childMap.set(filter.parentId, children);
      }
    }

    for (const filter of basicFilters) {
      if (filter.parentId == null) {
        sortedFilters.push(filter);
        const children = childMap.get(filter.id);
        if (children) {
          sortedFilters.push(...children);
        }
      }
    }

    return sortedFilters.map((filter) => {
      const isChild = filter.parentId != null;
      const available = isBasicFilterAvailable(filter);
      const alreadyInQuery = isBasicFilterAlreadyInQuery(filter.id);
      const isDisabled = filter.disabled || !available || alreadyInQuery;

      return (
        <div
          id={`addfiltermodal-basicfilter-${filter.id}-row`}
          data-testid={`addfiltermodal-basicfilter-${filter.id}-row`}
          key={filter.id}
          className={`flex flex-row items-center gap-2 ${isChild ? 'pl-6' : ''}`}
        >
          <div
            id={`addfiltermodal-basicfilter-${filter.id}-displaytext`}
            data-testid={`addfiltermodal-basicfilter-${filter.id}-displaytext`}
            className="flex-1 text-left"
          >
            {filter.displayText ?? filter.field}
          </div>
          <div className="shrink-0">
            <FmlxCheckBox
              id={`addfiltermodal-basicfilter-${filter.id}-checkbox`}
              checked={isFilterChecked(filter.id, true)}
              disabled={isDisabled}
              onChange={() => onToggleBasicFilter(filter.id)}
            />
          </div>
        </div>
      );
    });
  }, [
    basicFilters,
    selectedBasicFilterIds,
    existingBasicFilterIds,
    isBasicFilterAvailable,
    isBasicFilterAlreadyInQuery,
  ]);

  const tabs = [
    {
      title: 'BASIC',
      content: (
        <div
          id={`addfiltermodal-tab-basicfilter`}
          data-testid={`addfiltermodal-tab-basicfilter`}
          className="flex flex-col h-[45vh] min-h-0"
        >
          <div className="flex flex-row items-center">
            <div className="flex-1 text-left">Select All</div>
            <div className="shrink-0">
              <FmlxCheckBox
                id="addfiltermodal-basicfilter-selectall-checkbox"
                checked={areAllBasicFiltersSelected}
                indeterminate={isBasicFilterPartiallySelected}
                disabled={allSelectableIds.size === 0}
                onChange={onToggleSelectAll}
              />
            </div>
          </div>
          <div className="flex-1 flex-col min-h-0 overflow-y-auto overflow-x-hidden">
            {basicFilterCheckboxes}
          </div>
          <div className="h-4 flex-none">{tabFooter}</div>
        </div>
      ),
      disabled: false,
      icon: undefined,
    },
    {
      title: 'ADVANCED',
      content: (
        <div
          id={`addfiltermodal-tab-advancedfilter`}
          data-testid={`addfiltermodal-tab-advancedfilter`}
          className="flex flex-col h-[45vh] min-h-0"
        >
          <div className="flex-1 min-h-0 overflow-auto">{advancedFilterAccordions}</div>
          <div className="flex-none mt-2 h-4">{tabFooter}</div>
        </div>
      ),
      disabled: false,
      icon: undefined,
    },
  ];

  const okButton = {
    show: true,
    disabled: false,
    label: 'ADD',
    type: 'primary',
    variant: 'contain',
    onClick: onConfirmClicked,
  };

  const cancelButton = {
    show: true,
    disabled: false,
    label: 'CANCEL',
    type: 'basic',
    variant: 'outline',
    onClick: onCancelClicked,
  };

  const onTabChange = (value: { index: number | string }) => {
    if (typeof value.index === 'number') {
      setSelectedTab(value.index);
    }

    const parsed = Number(value.index);
    if (parsed != null) {
      setSelectedTab(parsed);
    }
  };

  return (
    <div id={id} data-testid={id} className="flex flex-col min-h-0">
      <FmlxModal
        id={`addfiltermodal-fmlxmodal`}
        open={open}
        size={FmlxModal.Size.MEDIUM}
        title="Filter"
        onCloseClick={onCancelClicked}
        primaryButton={okButton as any}
        secondaryButton={cancelButton as any}
        tertiaryButton={{ show: false } as any}
      >
        <div className="flex flex-col min-h-0">
          <div className="flex-1 min-h-0 overflow-auto">
            <FmlxTab
              id={`addfiltermodal-fmlxtab`}
              variant={FmlxTab.Variant.STANDARD}
              selectedTab={selectedTab}
              items={tabs}
              onChange={onTabChange}
            />
          </div>
        </div>
      </FmlxModal>
    </div>
  );
}
